import { createPortal } from 'react-dom';
import { useAnchoredMenu } from './useAnchoredMenu';
import { useState } from 'react';
import { SCRIPT_STATUSES, STATUS_LABELS, type ScriptStatus } from '../../shared/types';
import Tooltip from './EditorTopBar/Tooltip';
import type { StatusPillLabel } from './EditorTopBar/statusLabels';

interface Props {
  inline?: boolean;
  current: ScriptStatus;
  onChange: (status: ScriptStatus) => Promise<void> | void;
  disabled?: boolean;
  /** Trigger wording, per width. The menu keeps the app-wide STATUS_LABELS. */
  labels: (status: ScriptStatus) => StatusPillLabel;
  /** Drop the "Status" prefix below md, where the rail needs every pixel. */
  hideLabelBelowMd?: boolean;
  /** Which statuses the menu offers (2A drops Draft after the first publish). */
  options?: ScriptStatus[];
}

export default function StatusToggle({
  current,
  inline = false,
  onChange,
  disabled,
  labels,
  hideLabelBelowMd = false,
  options = SCRIPT_STATUSES,
}: Props) {
  const menu = useAnchoredMenu({ width: 220, itemSelector: '[role="menuitem"]' });
  const [busy, setBusy] = useState(false);

  async function handleSelect(status: ScriptStatus) {
    if (busy) return;
    if (status === current) {
      menu.close({ focusAnchor: true });
      return;
    }
    setBusy(true);
    try {
      await onChange(status);
    } finally {
      setBusy(false);
      menu.close({ focusAnchor: true });
    }
  }

  const isApproved = current === 'approved';
  const label = labels(current);
  const prefixClass = hideLabelBelowMd ? 'hidden md:inline' : 'inline';

  if (inline) return <label className="mobile-status-select">Status
    <select value={current} disabled={disabled || busy} onChange={e => void handleSelect(e.target.value as ScriptStatus)}>
      {options.map(s => <option key={s} value={s}>{STATUS_LABELS[s]}</option>)}
    </select>
  </label>;

  return (
    <div className="relative shrink-0">
      <Tooltip content={`Status · ${label.full}`}>
        <button
          type="button"
          ref={menu.anchorRef}
          // aria-disabled rather than the disabled attribute: a disabled
          // button cannot take focus back when the menu closes mid-change.
          onClick={disabled || busy ? undefined : menu.toggle}
          aria-disabled={disabled || busy || undefined}
          aria-haspopup="menu"
          aria-expanded={menu.open}
          aria-label={`Status: ${label.full}`}
          className={`
            inline-flex items-center gap-1.5 h-[30px] px-2.5 md:px-3 rounded-full
            font-mono text-[10px] font-semibold uppercase tracking-[0.12em]
            whitespace-nowrap shrink-0 transition-shadow duration-200
            focus:outline-none focus-visible:shadow-focus
            aria-disabled:opacity-50 aria-disabled:cursor-not-allowed
            ${
              isApproved
                ? 'bg-accent/10 text-accent shadow-[inset_0_0_0_1px_color-mix(in_srgb,var(--color-accent)_35%,transparent)]'
                : `bg-card shadow-[inset_0_0_0_1px_var(--color-line)] hover:shadow-[inset_0_0_0_1px_var(--color-faint)] ${
                    current === 'draft' ? 'text-muted' : 'text-fg'
                  }`
            }
          `}
        >
          <span className={`font-medium ${isApproved ? 'opacity-70' : 'text-faint'} ${prefixClass}`}>
            Status
          </span>
          <span aria-hidden className={`${isApproved ? 'opacity-70' : 'text-faint'} ${prefixClass}`}>
            ·
          </span>
          {/* Three spans, one per width: the pill has to shed characters as the
              rail claims the row, and doing it in CSS keeps it resize-proof. */}
          <span className="hidden lg:inline">{label.full}</span>
          <span className="hidden md:inline lg:hidden">{label.short}</span>
          <span className="md:hidden">{label.phone}</span>
          <svg
            width="10"
            height="10"
            viewBox="0 0 10 10"
            fill="none"
            aria-hidden
            className={`transition-transform duration-200 ${menu.open ? 'rotate-180' : ''}`}
          >
            <path d="M2 4l3 3 3-3" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        </button>
      </Tooltip>

      {menu.open && menu.popupStyle && createPortal(
        <div
          ref={menu.menuRef}
          style={menu.popupStyle}
          role="menu"
          className="
            py-1.5
            bg-card border border-line rounded-sm
            shadow-flare-lg/30
            z-30 animate-rise-subtle
          "
        >
          {options.map((s) => {
            const isCurrent = s === current;
            return (
              <button
                key={s}
                type="button"
                role="menuitem"
                onClick={() => handleSelect(s)}
                aria-disabled={busy || undefined}
                className={`
                  w-full text-left px-3 py-2
                  flex items-center justify-between gap-3
                  transition-colors duration-150
                  ${isCurrent ? 'bg-elevated text-accent' : 'text-fg hover:bg-elevated'}
                `}
              >
                <span className="flex items-center gap-2">
                  <span
                    className={`w-1.5 h-1.5 rounded-full ${
                      isCurrent ? 'bg-accent' : 'bg-faint/40'
                    }`}
                    aria-hidden
                  />
                  <span className="font-medium text-[13px]">{STATUS_LABELS[s]}</span>
                </span>
                <span className="font-mono text-[10px] uppercase tracking-[0.12em] text-faint">
                  {hint(s)}
                </span>
              </button>
            );
          })}
        </div>,
        document.body,
      )}
    </div>
  );
}

function hint(status: ScriptStatus): string {
  switch (status) {
    case 'draft':
      return 'internal';
    case 'v1':
      return 'sent';
    case 'v2':
      return 'rev 2';
    case 'v3':
      return 'rev 3';
    case 'approved':
      return 'final';
  }
}
